import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { HomeComponent } from './pages/home/home.component';
import { ListingComponent } from './pages/listing/listing.component';
import { PdpComponent } from './pages/pdp/pdp.component';
import { CartComponent } from './pages/cart/cart.component';
import { WishlistComponent } from './pages/wishlist/wishlist.component';
import { CustomUploadComponent } from './pages/custom-upload/custom-upload.component';
import { CustomDesignComponent } from './pages/custom-design/custom-design.component';
import { NurComponent } from './pages/nur/nur.component';
import { PrintFrameComponent } from './pages/print-frame/print-frame.component';
import { CollageSetComponent } from './pages/collage-set/collage-set.component';
import { CollageSetDetailComponent } from './pages/collage-set-detail/collage-set-detail.component';
import { ArtReadyComponent } from './pages/art-ready/art-ready.component';
import { WallArtComponent } from './pages/wall-art/wall-art.component';
import { CheckoutComponent } from './pages/checkout/checkout.component';
import { ThankYouComponent } from './pages/thank-you/thank-you.component';
// PaymentSuccess (the old PayU redirect-return page) stays deleted — it called
// the unauthenticated /api/unauth/paymentHook. PaymentFailed is REBUILT for the
// Razorpay flow: it calls no backend, just shows the gateway's failure and
// offers a retry. Checkout routes here on a genuine failure.
import { PaymentFailedComponent } from './pages/payment-failed/payment-failed.component';
import { SimilarDialogComponent } from './components/similar-dialog/similar-dialog.component';
import { AboutComponent } from './pages/about/about.component';
import { ContactComponent } from './pages/contact/contact.component';
import { PartnersComponent } from './pages/partners/partners.component';
import { FindArtComponent } from './pages/find-art/find-art.component';
import { ArtSearchComponent } from './pages/art-by-search/art-search.component';
import { OnSaleDetailComponent } from './pages/featured-art-details/on-sale-detail.component';
import { ProductCustomizationComponent } from './pages/product-customization/product-customization.component';
import { CategoryListingComponent } from './pages/category-listing/category-listing.component';
import { FeaturedFramesComponent } from './pages/featured-frames/featured-frames.component';
import { FeaturedFrameDetailComponent } from './pages/featured-frame-detail/featured-frame-detail.component';
import { ArtForBusinessComponent } from './pages/art-for-business/art-for-business.component';
import { FaqComponent } from './pages/faq/faq.component';
import { PrivacyPolicyComponent } from './pages/privacy-policy/privacy-policy.component';
import { TermsOfUseComponent } from './pages/terms-of-use/terms-of-use.component';
import { ReturnPolicyComponent } from './pages/return-policy/return-policy.component';
import { PrintingPartnerComponent } from './pages/printing-partner/printing-partner.component';
import { CareerComponent } from './pages/career/career.component';
import { SpaceListingComponent } from './pages/space-listing/space-listing.component';
import { LandingComponent } from './pages/landing/landing.component';
import { AuthGuard } from './guards/auth.guard';

const routes: Routes = [
  { path: '', component: LandingComponent },
  { path: 'wallpaper-home', component: HomeComponent },
  {
    path: 'account',
    loadChildren: () => import('./pages/account/account.module').then(m => m.AccountModule),
    canActivate: [AuthGuard]
  },
  { path: 'wall-art', component: WallArtComponent },
  {
    path: 'wall-art/art-by-search/:category/:name',
    component: ArtSearchComponent
  },
  { path: 'listing', component: ListingComponent },
  { path: 'wallpaper/category/:slug', component: CategoryListingComponent },
  { path: 'wallpaper/space/:slug', component: SpaceListingComponent },
  { path: 'art-ready', component: ArtReadyComponent },
  { path: 'print-frame', redirectTo: 'print-frame/photo-frame', pathMatch: 'full' },
  { path: 'print-frame/photo-frame',    component: PrintFrameComponent, data: { page: 'photo-frame' } },
  { path: 'print-frame/only-print',     component: PrintFrameComponent, data: { page: 'only-print' } },
  { path: 'print-frame/frame-only',     component: PrintFrameComponent, data: { page: 'frame-only' } },
  { path: 'print-frame/canvas',         component: PrintFrameComponent, data: { page: 'canvas' } },
  { path: 'print-frame/collage-set',    component: CollageSetComponent },
  { path: 'collage-set/:slug',         component: CollageSetDetailComponent },
  { path: 'collage-set',               component: CollageSetComponent, pathMatch: 'full' },
  { path: 'print-frame/featured-frames',component: FeaturedFramesComponent },
  { path: 'featured-frames',            component: FeaturedFramesComponent },
  { path: 'featured-frame/:caption',    component: FeaturedFrameDetailComponent },
  { path: 'product-customization', component: ProductCustomizationComponent },
  // Primary PDP route: /wallpaper/{collection_slug}/{slug}/{max_color}-{min_color}
  // Primary PDP route: /wallpaper/{slug} — PDP resolves full data via meta endpoint
  { path: 'wallpaper/:slug', component: PdpComponent },
  // 3-segment route: /wallpaper/{collection_slug}/{slug}/{max_color}-{min_color}
  { path: 'wallpaper/:collection_slug/:slug/:colors', component: PdpComponent },
  // 2-segment fallback: /wallpaper/{slug_or_id}/{colors}
  { path: 'wallpaper/:id/:colors', component: PdpComponent },
  // ID/slug-only fallback for cart/wishlist links
  { path: 'pdp/:id', component: PdpComponent },
  { path: 'cart', redirectTo: 'checkout', pathMatch: 'full' },
  { path: 'wishlist', component: WishlistComponent },
  { path: 'custom-upload', component: CustomUploadComponent },
  { path: 'custom-design', component: CustomDesignComponent },
  { path: 'custom-design/:sku', component: CustomDesignComponent },
  { path: 'checkout', component: CheckoutComponent },
  { path: 'buy-now', component: CheckoutComponent, data: { buyNow: true } },
  { path: 'thank-you', component: ThankYouComponent },
  { path: 'payment-failed', component: PaymentFailedComponent },
  { path: 'similar/:slug', component: SimilarDialogComponent },
  { path: 'about', component: AboutComponent },
  { path: 'contact', component: ContactComponent },
  { path: 'partner', component: PartnersComponent },
  { path: 'find-art', component: FindArtComponent },
  { path: 'featured-art/:caption', component: FeaturedFrameDetailComponent },
  { path: 'art-for-business', component: ArtForBusinessComponent },
  { path: 'faq', component: FaqComponent },
  { path: 'privacy-policy', component: PrivacyPolicyComponent },
  { path: 'terms-of-use', component: TermsOfUseComponent },
  { path: 'return-policy', component: ReturnPolicyComponent },
  { path: 'printing-partner', component: PrintingPartnerComponent },
  { path: 'career', component: CareerComponent },
  { path: '**', redirectTo: '' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    useHash: false,
    scrollPositionRestoration: 'top',
    // CRITICAL: without this, clicking a "similar product" card on the PDP navigates
    // to the same route pattern (/wallpaper/:c/:s/:colors) with different params but
    // Angular won't re-run ngOnInit — only paramMap subscription fires.
    // onSameUrlNavigation:'reload' also prevents stale data when the user presses
    // the browser Back button to the same PDP URL they just left.
    onSameUrlNavigation: 'reload',
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
